import { MoreVertical, RotateCcw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  Popover,
  PopoverMenu,
  PopoverMenuItem,
  PopoverTrigger,
} from '@/components/ui/Popover/Popover'
import { ApiError } from '@/lib/api/jsonFetch'
import { useArchivePickup, useRestorePickup } from '../api/queries'
import type { PickupSummary } from '../api/types'

/**
 * Per-card / per-row ⋯ menu for dismissing duplicate pickup requests.
 * Modeled on QuoteActionsMenu. Distinct from the board's terminal-stage
 * "Archived" concept — this is a manual duplicate soft-delete, so the
 * label is "Mark as duplicate" rather than "Archive"/"Delete".
 */

export interface PickupActionsMenuProps {
  row: PickupSummary
}

export function PickupActionsMenu({ row }: PickupActionsMenuProps) {
  const archive = useArchivePickup()
  const restore = useRestorePickup()

  const isDuplicate = row.deleted_at !== null
  const pending = archive.isPending || restore.isPending

  function handleArchive() {
    if (
      !window.confirm(
        'Mark this pickup as a duplicate? It will be removed from the pipeline board. ' +
          'Use this only when the same pickup already exists as another card — ' +
          'you can restore it later from the Duplicates view.',
      )
    ) {
      return
    }
    archive.mutate(
      { pickupId: row.id, reason: 'duplicate' },
      {
        onSuccess: () => toast.success('Marked as duplicate — removed from board'),
        onError: (err) =>
          toast.error(err instanceof ApiError ? err.message : 'Could not mark as duplicate'),
      },
    )
  }

  function handleRestore() {
    restore.mutate(row.id, {
      onSuccess: () => toast.success('Restored to pipeline'),
      onError: (err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not restore'),
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Pickup actions"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          disabled={pending}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MoreVertical size={14} />
        </button>
      </PopoverTrigger>
      <PopoverMenu align="end" onClick={(e) => e.stopPropagation()}>
        {isDuplicate ? (
          <PopoverMenuItem onSelect={handleRestore}>
            <RotateCcw size={12} />
            Restore to pipeline
          </PopoverMenuItem>
        ) : (
          <PopoverMenuItem onSelect={handleArchive}>
            <Trash2 size={12} />
            Mark as duplicate
          </PopoverMenuItem>
        )}
      </PopoverMenu>
    </Popover>
  )
}
